/**
 * What this device's person may do in one record, read off the catalog row the
 * last pull wrote.
 *
 * A COURTESY, NEVER A FENCE. Everything here decides whether a control is
 * drawn, hidden, or relabelled. No screen may skip a request because this
 * module said no: the server refuses regardless, and it is the only thing that
 * does. `db:verify:masking` and `db:verify:sync` prove the server half; nothing
 * on a phone can prove anything about a phone somebody else controls.
 *
 * THREE RULES, and the second is the one that gets forgotten.
 *
 * 1. Unknown means ALLOWED. Null columns are a device that has not synced since
 *    migration 8 shipped. Hiding a button on missing data locks a person out of
 *    their own records after an upgrade, offline, and the server would have let
 *    them through. Offer it and let the server refuse — the refusal is a
 *    sentence, and `TeamOutcome` already carries one.
 * 2. An owner is null, not a full list. `grantSummaryOf` on the server
 *    short-circuits an owner to null rather than materialising eight strings
 *    per record per pull, so "no grant" and "every grant" are the same value
 *    here on purpose. Rule 1 is what makes that safe.
 * 3. A grant edit does not bump `field_set_ver`. Sync only resends schemas
 *    whose version differs, so a revoked capability arrives with the CATALOG
 *    and not with the schema. Take it from the catalog, which is what this
 *    module reads.
 *
 * Pure, no React, no database handle. `record-grant.test.ts` beside it.
 */
import type { EntryReaches, GrantRow, Reach, RecordCapability, RecordGrantSummary } from '@alluzo/shared';

/**
 * The five columns migration 8 added and the one migration 9 did, as any
 * catalog row carries them.
 *
 * Structural rather than the Drizzle row type, so a test can pass a literal and
 * a caller can pass a row it selected three columns of.
 */
export type GrantColumns = {
  readonly grantCapabilities: string | null;
  readonly grantRowScope: 'all' | 'own_entries' | null;
  readonly grantWriteMode: 'direct' | 'submit_draft' | 'request' | null;
  readonly grantBalanceVisible: boolean | null;
  readonly grantDirection: 'in_only' | 'out_only' | null;
  /** A JSON array of field ids, or null for every field. */
  readonly grantWritableFields: string | null;
  /**
   * Migration 13: the summary's `reach` as JSON, the authors Their team
   * covers as a JSON array, and the digest of the fields hidden here. Optional
   * so a caller that selected the first six still fits; absent or null is
   * unknown, and unknown narrows nothing.
   */
  readonly grantReach?: string | null;
  readonly grantTeam?: string | null;
  readonly grantMask?: string | null;
};

const REACHES: readonly Reach[] = ['off', 'own', 'team', 'everyone'];

const isReach = (value: unknown): value is Reach => typeof value === 'string' && (REACHES as readonly string[]).includes(value);

/** The four reaches back out of their column, or null when any of them cannot be read: rule 1. */
const readReach = (held: string | null | undefined): EntryReaches | null => {
  if (held === null || held === undefined) return null;
  try {
    const parsed = JSON.parse(held) as Record<string, unknown> | null;
    if (parsed === null || typeof parsed !== 'object') return null;
    const { see, change, delete: remove, download } = parsed;
    if (!isReach(see) || !isReach(change) || !isReach(remove) || !isReach(download)) return null;
    return { see, change, delete: remove, download };
  } catch {
    return null;
  }
};

/**
 * A JSON list of strings, or null for anything else. Rule 1 for the fallback:
 * a list this device cannot read narrows nothing.
 */
const readStringList = (held: string | null): string[] | null => {
  if (held === null) return null;
  try {
    const parsed: unknown = JSON.parse(held);
    if (!Array.isArray(parsed)) return null;
    return parsed.filter((one): one is string => typeof one === 'string');
  } catch {
    return null;
  }
};

/**
 * The wire shape back out of the six columns, or null for "everything".
 *
 * `grantCapabilities` is the one that decides. The other five are dials on a
 * grant that exists, so a row with a capability list and no write mode is a
 * real grant on an older payload, not an owner.
 */
export const unpackGrant = (row: GrantColumns): RecordGrantSummary | null => {
  if (row.grantCapabilities === null) return null;
  // A column this device wrote itself cannot normally be unparseable. If it
  // ever is, rule 1 decides: answer "everything" and let the server refuse,
  // rather than locking somebody out on a corrupted local string.
  const capabilities = readStringList(row.grantCapabilities);
  if (capabilities === null) return null;
  const writable = readStringList(row.grantWritableFields);
  return {
    capabilities: capabilities as RecordCapability[],
    rowScope: row.grantRowScope ?? 'all',
    writeMode: row.grantWriteMode ?? 'direct',
    balanceVisible: row.grantBalanceVisible ?? true,
    direction: row.grantDirection,
    // An empty family does not narrow, the same reading the server's fence makes.
    writableFields: writable === null || writable.length === 0 ? null : writable,
    ...optionalParts(row),
  };
};

/** The three parts a newer server adds, each only when it was stored and can be read. */
const optionalParts = (row: GrantColumns): Pick<RecordGrantSummary, 'reach' | 'team' | 'maskKey'> => {
  const reach = readReach(row.grantReach);
  const team = readStringList(row.grantTeam ?? null);
  const mask = row.grantMask ?? null;
  return {
    ...(reach === null ? {} : { reach }),
    ...(team === null ? {} : { team }),
    ...(mask === null ? {} : { maskKey: mask }),
  };
};

/**
 * The six columns from the wire shape, ready for an insert or an update.
 *
 * `undefined` is accepted beside null because the type says `grant` is always
 * there and the WIRE does not: a server older than this build sends a payload
 * with no such key. Both mean unknown, and unknown is stored as null. The same
 * goes for `writableFields` on its own: a server one release behind sends a
 * grant without it, and that grant narrows no field.
 */
export const packGrant = (grant: RecordGrantSummary | null | undefined): GrantColumns =>
  grant === null || grant === undefined
    ? {
        grantCapabilities: null,
        grantRowScope: null,
        grantWriteMode: null,
        grantBalanceVisible: null,
        grantDirection: null,
        grantWritableFields: null,
        grantReach: null,
        grantTeam: null,
        grantMask: null,
      }
    : {
        grantCapabilities: JSON.stringify(grant.capabilities),
        grantRowScope: grant.rowScope,
        grantWriteMode: grant.writeMode,
        grantBalanceVisible: grant.balanceVisible,
        grantDirection: grant.direction,
        grantWritableFields:
          grant.writableFields === null || grant.writableFields === undefined
            ? null
            : JSON.stringify(grant.writableFields),
        // Absent from an older server's summary, and stored as unknown.
        grantReach: grant.reach === undefined ? null : JSON.stringify(grant.reach),
        grantTeam: grant.team === undefined ? null : JSON.stringify(grant.team),
        grantMask: grant.maskKey ?? null,
      };

/**
 * The fields a new-entry form should draw for this person: every one when the
 * grant does not narrow, and exactly the family when it does.
 *
 * Pure, and the shape is structural so the form's own field type fits. The
 * courtesy half of the server's writable_fields fence: a field left undrawn
 * is a field never sent, so the save is never refused with FIELD_NOT_WRITABLE
 * — which offline is a 4xx and a dropped outbox row. The server still refuses
 * regardless, and it is the only thing that does.
 */
export const writableOnly = <Field extends { readonly id: string }>(
  fields: readonly Field[],
  writableFields: readonly string[] | null,
): Field[] => {
  if (writableFields === null) return [...fields];
  const family = new Set(writableFields);
  return fields.filter((field) => family.has(field.id));
};

/**
 * Whether to OFFER one action in this record. True on a null grant, per rule 1.
 *
 * The name says `may` and not `can` on purpose: it answers what to draw, never
 * what will happen.
 */
export const mayInRecord = (row: GrantColumns, capability: RecordCapability): boolean => {
  const grant = unpackGrant(row);
  return grant === null || grant.capabilities.includes(capability);
};

/** What the save button should SAY. `direct` on a null grant, per rule 1. */
export const writeModeOf = (row: GrantColumns): 'direct' | 'submit_draft' | 'request' =>
  unpackGrant(row)?.writeMode ?? 'direct';

/** Whether to draw the summary header. True on a null grant, per rule 1. */
export const balanceVisibleIn = (row: GrantColumns): boolean =>
  unpackGrant(row)?.balanceVisible ?? true;

/**
 * Whether the rows of other people reach this device at all.
 *
 * Not a control to hide: the server already withheld those rows from the pull,
 * so the list is short whatever this answers. It is here so a screen can SAY
 * "your entries" instead of letting a person wonder where the rest went.
 */
export const seesOwnRowsOnly = (row: GrantColumns): boolean =>
  unpackGrant(row)?.rowScope === 'own_entries';

/* ------------------------------------------------------- whose rows stay */

/**
 * Whose synced rows of one record this person may hold on the device: every
 * row, none of them, or only the rows these people wrote. The mirror's half of
 * the server's reach (`entryReachFrom` in apps/server/src/access.ts), which
 * `pruneUnreadableRows` applies and a re-read compares.
 *
 * `reach` decides when the summary carries it: Off holds no row, Own the
 * person's own, Their team the rows of everyone in `team`, Everyone every row.
 * Without it, the old words decide as they always did. Rule 1 throughout: a
 * null grant, a team the server did not send, or no identity to compare
 * against keeps every row, because deleting on a guess takes rows the person
 * may read and the cursor has already moved past them.
 */
export type RowsKept = 'all' | 'none' | readonly string[];

export const rowsKept = (grant: RecordGrantSummary | null, me: string | null): RowsKept => {
  if (grant === null) return 'all';
  if (!grant.capabilities.includes('view_all_entries')) return 'none';
  const see: Reach | null = grant.reach?.see ?? (grant.rowScope === 'own_entries' ? 'own' : null);
  switch (see) {
    case 'off':
      return 'none';
    case 'own':
      return me === null ? 'all' : [me];
    case 'team':
      if (grant.team === undefined || me === null) return 'all';
      return [...new Set([me, ...grant.team])];
    default:
      return 'all';
  }
};

/** Whether `after` keeps a row `before` did not: a record, a person or a reach was added. */
const keepsMore = (before: RowsKept, after: RowsKept): boolean => {
  if (before === 'all') return false;
  if (after === 'all') return true;
  if (after === 'none') return false;
  if (before === 'none') return true;
  const held = new Set(before);
  return after.some((author) => !held.has(author));
};

/**
 * Why one record's rows on this device must be read again from the start, or
 * null when the pull already brings everything that changed.
 *
 *   widened  the person now reads rows they did not: Own became Their team, a
 *            report joined their team, a role that saw nothing now sees.
 *            Those rows are older than the cursor, so no pull ever sends them.
 *   masked   the fields hidden from them here moved. The schema's version did
 *            not, so the device holds a schema, rows and totals cut to the
 *            old mask; a field now at None would still show its old values.
 *
 * `stored` is the catalog row as it was before this pull wrote it. A column
 * a build before migration 13 left null is unknown, so its reach is read from
 * the old words, which never ask for a re-read on their own.
 */
export const rereadReason = (
  stored: GrantColumns,
  next: RecordGrantSummary | null,
  me: string | null,
): 'widened' | 'masked' | null => {
  if ((stored.grantMask ?? null) !== (next?.maskKey ?? null)) return 'masked';
  return keepsMore(rowsKept(unpackGrant(stored), me), rowsKept(next, me)) ? 'widened' : null;
};

/* ------------------------------------------------------------ the screen */

export type RecordPermissions = {
  /** Whether to OFFER one action. True on an unknown grant, which is the rule. */
  readonly may: (capability: RecordCapability) => boolean;
  readonly writeMode: 'direct' | 'submit_draft' | 'request';
  readonly balanceVisible: boolean;
  /** True when other people's rows never reach this device for this record. */
  readonly ownRowsOnly: boolean;
  /**
   * Which way this person's money may point. Null — an owner, or an unsynced
   * row — narrows nothing, per rule 1. The entry form reads it to leave the
   * refused sign option undrawn; the server refuses it regardless
   * (DIRECTION_NOT_ALLOWED, db:verify:fences).
   */
  readonly direction: 'in_only' | 'out_only' | null;
  /**
   * The field ids this person may write. Null — an owner, a grant that does
   * not narrow, or an unsynced row — narrows nothing, per rule 1. The new-entry
   * form draws only these when the list is there (`writableOnly`); the server
   * refuses the rest regardless (FIELD_NOT_WRITABLE, db:verify:fences).
   */
  readonly writableFields: readonly string[] | null;
};

/**
 * The six capabilities that change a record. Refused, all of them, while the
 * app is drawn through somebody else's eyes.
 *
 * `view_summaries` and `view_all_entries` are not here: a read stays a read,
 * and the person looking is entitled to what is already on their own phone.
 */
const WRITES: readonly RecordCapability[] = [
  'add_entry',
  'edit_own_entry',
  'edit_any_entry',
  'delete_entry',
  'edit_structure',
  'export',
];

/**
 * The write fence View as puts in front of a grant.
 *
 * Pure, and exported for the test. Every control on a record screen already
 * asks `may()` before it is drawn, so refusing every write here is what makes
 * the whole app read-only in one place, and no screen has to learn a new prop.
 * The reads pass through untouched: in Phase 1 the person sees their own
 * records and their own numbers, with every door that writes taken off.
 */
export const fenced = (permissions: RecordPermissions, viewingAsSomeone: boolean): RecordPermissions =>
  viewingAsSomeone
    ? {
        ...permissions,
        may: (capability) => !WRITES.includes(capability) && permissions.may(capability),
      }
    : permissions;

/* ----------------------------------------------------- through their eyes */

/**
 * One member's GrantRow as the permissions their screen would answer. The
 * whole point of View as: the owner SEES the restriction, not their own view
 * dressed in somebody's name.
 *
 * Rule 1 (unknown means allowed) deliberately does NOT apply here, and this is
 * the one place it must not: an unknown grant on the owner's own catalog is a
 * device that has not synced, but a record with no row in the MEMBER'S grant
 * list is a record they cannot open — deny by default, the server's own rule.
 * `fenced()` still runs on top, so the writes stay off whatever the row says.
 */
export const throughTheirEyes = (grant: GrantRow | null): RecordPermissions => ({
  may: (capability) => grant !== null && grant.grants.includes(capability),
  writeMode: grant?.writeMode ?? 'direct',
  balanceVisible: grant?.balanceVisible === true,
  ownRowsOnly: grant?.rowScope === 'own_entries',
  direction: grant?.direction ?? null,
  writableFields:
    grant === null || grant.writableFields === null || grant.writableFields.length === 0
      ? null
      : grant.writableFields,
});

/** As much of a catalog row as the lens below needs. Structural, for tests. */
type LensedRecordRow = { readonly id: string; readonly headlineValue: number | null };

/**
 * The record catalog as one member sees it: only the records their grants name,
 * and no headline where their `balance_visible` is off. The same two rules the
 * server applies to THEIR pull, replayed over the owner's local mirror.
 */
export const catalogThroughTheirEyes = <Row extends LensedRecordRow>(
  rows: readonly Row[],
  grants: Readonly<Record<string, GrantRow>>,
): Row[] =>
  rows
    .filter((row) => grants[row.id] !== undefined)
    .map((row) => (grants[row.id]!.balanceVisible ? row : { ...row, headlineValue: null }));

/** As much of one entry as the lens below needs. Structural, for tests. */
type LensedEntryRow = {
  readonly createdBy: string;
  readonly values: Readonly<Record<string, unknown>>;
};

/**
 * One record's rows as the member would receive them: nothing without a grant,
 * only their own rows under `own_entries`, only their team's where their role
 * reaches Their team (`team`: the member and everyone below them), and a field their `field_policy`
 * hides is ABSENT from the values — absent, not null, the same claim the
 * server's mask makes. (`app.field.hidden_for` is not replayed here: it holds
 * role names the device does not carry. A grant that hides by job alone will
 * still show that field in this mode — noted in the plan, not silently fine.)
 */
export const entriesThroughTheirEyes = <Row extends LensedEntryRow>(
  rows: readonly Row[],
  grant: GrantRow | null,
  memberUserId: string,
  /** Whose rows reach them in this record when their See entries is Their team; null when it is not. */
  team: ReadonlySet<string> | null = null,
): Row[] => {
  if (grant === null || !grant.grants.includes('view_all_entries')) return [];
  const kept =
    grant.rowScope === 'own_entries'
      ? rows.filter((row) => row.createdBy === memberUserId)
      : team !== null
        ? rows.filter((row) => row.createdBy === memberUserId || team.has(row.createdBy))
        : [...rows];

  const hidden = Object.entries(grant.fieldPolicy)
    .filter(([, policy]) => policy?.hidden === true)
    .map(([fieldId]) => fieldId);
  if (hidden.length === 0) return kept;

  return kept.map((row) => {
    const values: Record<string, unknown> = { ...row.values };
    for (const fieldId of hidden) delete values[fieldId];
    return { ...row, values };
  });
};
